import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import { PlusIcon, SearchIcon } from "lucide-react";
import React, {
  Fragment,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { getHiringCompanyMembers } from "@endpoints/hiringCompany/hiringCompany";
import { IHiringStaff } from "@endpoints/types/hiringStaff.type";
import { updateJobRequirement } from "@endpoints/hiringCompany/requirements";
import { useSearchParams } from "react-router-dom";
import InviteMemberDialog from "@pages/hiringCompany/HiringStaffMembers/components/InviteMemberDialog";
import { MeetingContext } from "../CreateMeetingPage";

type SelectTeamMemberDialogProps = {
  openDialog: boolean;
  setOpenDialog: React.Dispatch<SetStateAction<boolean>>;
};

const SelectTeamMemberDialog = (props: SelectTeamMemberDialogProps) => {
  const { openDialog, setOpenDialog } = props;
  const { fetchMeetingRequirements } = useContext(MeetingContext);
  const [searchParams] = useSearchParams();
  const jobRequirementId = searchParams?.get("jobRequirementId");

  const [staffMembers, setStaffMembers] = useState<IHiringStaff[]>([]);
  const [isStaffMembersLoading, setIsStaffMembersLoading] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [isAddingHiringTeam, setIsAddingHiringTeam] = useState(false);
  const [showInviteMemberDialog, setShowInviteMemberDialog] = useState(false);

  async function fetchHiringCompanyMembers() {
    try {
      setIsStaffMembersLoading(true);
      const { success, data } = await getHiringCompanyMembers();
      if (success) {
        setStaffMembers(data?.members);
        console.log("hiring staff members", data);
      }
    } catch (error) {
      console.error("Error: fetching hiring company members", error);
    } finally {
      setIsStaffMembersLoading(false);
    }
  }

  const handleAddHiringTeamMembers = async () => {
    try {
      setIsAddingHiringTeam(true);
      const { success, data } = await updateJobRequirement({
        id: jobRequirementId,
        hiringTeamMembers: selectedTeamMembers?.map((item) => {
          return { userId: item };
        }),
      });
      if (success) {
        fetchMeetingRequirements(true);
      }
    } catch (error) {
      console.log(
        "Error: Adding hiring team members form create meeting page",
        error
      );
    } finally {
      setIsAddingHiringTeam(false);
      setOpenDialog(false);
    }
  };

  useEffect(() => {
    fetchHiringCompanyMembers();
  }, []);

  return (
    <Fragment>
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="bg-white !w-[50%] !max-w-[50%]">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Select team members to add to your hiring team
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col flex-1 h-[28rem]">
            {/* Search container */}
            <div className="flex items-center justify-between bg-gray-100 rounded-md border px-5 py-2.5">
              <div className="flex items-center bg-white pr-3 border rounded-md w-2/3">
                <Input
                  className="!focus-visible:ring-[3px] border-none !text-xs font-medium"
                  placeholder="Search by agent name,reference..."
                  // type="number"
                />
                <SearchIcon className="w-4 h-4" color="#2e2e2e" />
              </div>
              <button
                onClick={() => setShowInviteMemberDialog(true)}
                className="flex items-center justify-center rounded-md py-2 px-3 gap-2 bg-primary hover:bg-black text-white font-semibold text-sm"
              >
                <PlusIcon className="w-4 h-4" color="white" />
                Invite Members
              </button>
            </div>
            {/* Staff List container */}
            <div className="flex flex-col flex-1 min-h-0">
              {/* Header */}
              <div className="grid grid-cols-[0.2fr_1fr_1fr_0.5fr] py-3 border-b">
                <div />
                <span className="font-semibold text-sm text-light-black">
                  Team members
                </span>
                <span className="font-semibold text-sm text-light-black">
                  Designation
                </span>
                <span className="font-semibold text-sm text-light-black">
                  Reference
                </span>
              </div>
              {/* Body */}
              <div className="flex flex-col flex-1 min-h-0 overflow-y-scroll no-scrollbar">
                {staffMembers?.map((member) => {
                  const isSelected = selectedTeamMembers?.includes(member?._id);
                  return (
                    <div className="grid grid-cols-[0.2fr_1fr_1fr_0.5fr] py-3 items-center">
                      <div className="flex items-center justify-center">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() =>
                            setSelectedTeamMembers((prev) => {
                              if (isSelected) {
                                return prev?.filter(
                                  (item) => item !== member?._id
                                );
                              } else {
                                return [...prev, member?._id];
                              }
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        {member?.profilePic ? (
                          <img src={member?.profilePic} className="w-8 h-8" />
                        ) : (
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 font-medium text-gray-500">
                            {member?.firstName.charAt(0)}
                          </div>
                        )}
                        <span className="font-medium text-xs text-light-black">
                          {member?.firstName} {member?.lastName}
                        </span>
                      </div>
                      <span className="font-medium text-light-black text-xs">
                        Member
                      </span>
                      <span className="font-medium text-light-black text-xs">
                        {member?.refNo}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleAddHiringTeamMembers}
              isLoading={isAddingHiringTeam}
              disabled={!selectedTeamMembers?.length}
              type="submit"
              className="text-white gap-3"
            >
              Add to Hiring Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* InviteMemberDialog */}
      <InviteMemberDialog
        isInviteDialogOpen={showInviteMemberDialog}
        setIsInviteDialogOpen={setShowInviteMemberDialog}
      />
    </Fragment>
  );
};

export default SelectTeamMemberDialog;
